/**
 * ClientDossierManagement — Unified Client Dossier Pro
 * ========================================================
 * Tabs (top):
 *   Dashboard | Clients | Waivers | Overdue Aging | Audit Log
 * Client detail view (side panel / route):
 *   Overview | Operations | Installments | Documents | Waivers | Statement | Audit
 *
 * All data is read from /api/client-dossier/* (branch-scoped by backend).
 * NO legacy data is ever mutated by this component.
 */
import React, { useState, useEffect, useContext, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from './ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Backend enum values (must match reason_category on backend)
const WAIVER_REASONS = [
  { value: 'accident', label: '⚠️ حادث' },
  { value: 'death', label: '⚰️ وفاة' },
  { value: 'disability', label: '♿ عجز' },
  { value: 'medical', label: '🏥 حالة صحية' },
  { value: 'financial_hardship', label: '💸 عدم قدرة مالية' },
  { value: 'social_case', label: '🏘️ حالة اجتماعية' },
  { value: 'loyal_client', label: '💚 عميل وفي' },
  { value: 'other', label: '📝 أخرى' },
];

const DOC_CATEGORIES = [
  { value: 'personal', label: '🧾 وثائق شخصية' },
  { value: 'contract', label: '📜 وثائق تعاقدية' },
  { value: 'payment', label: '💳 وثائق دفع وضمان' },
  { value: 'professional', label: '👔 وثائق مهنية ومالية' },
  { value: 'exceptional', label: '🚨 وثائق استثنائية' },
];

const STATUS_BADGE = (flag) => {
  const map = {
    fully_paid: { label: 'مكتمل السداد', color: 'bg-emerald-100 text-emerald-800' },
    in_progress: { label: 'قيد التسديد', color: 'bg-amber-100 text-amber-800' },
    no_payment: { label: 'لم يدفع', color: 'bg-red-100 text-red-800' },
    overdue: { label: 'متأخر', color: 'bg-red-200 text-red-900 font-bold' },
    partially_waived: { label: 'إعفاء جزئي', color: 'bg-violet-100 text-violet-800' },
    fully_waived: { label: 'معفى كلياً', color: 'bg-purple-100 text-purple-800' },
  };
  const m = map[flag] || { label: flag, color: 'bg-gray-100' };
  return <Badge className={m.color + ' mr-1 mb-1'}>{m.label}</Badge>;
};

const fmt = (n) => (typeof n === 'number' ? n.toLocaleString('en') : n);

// ============================================================================
export default function ClientDossierManagement({ user, activeTab, setActiveTab }) {
  const [tab, setTab] = useState('dashboard');
  const [dashboard, setDashboard] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null); // opened client detail

  useEffect(() => {
    const token = localStorage.getItem('token');
    axios.get(`${API}/client-dossier/reports/dashboard`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(r => setDashboard(r.data)).catch(e => console.warn(e));
  }, []);

  // Role gating (defense in depth — backend also enforces)
  const canView = user && ['super_admin', 'general_manager',
      'general_accountant', 'accountant', 'services_manager',
      'agency_manager', 'staff', 'employee'].includes(user.role);
  if (!canView) {
    return (
      <div className="p-8 text-center" data-testid="dossier-locked">
        <div className="text-6xl">🔒</div>
        <div className="mt-4 text-lg">ليس لديك صلاحية للوصول لملفات العملاء</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dossier-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">🗂️ ملفات العملاء (Pro)</h1>
          <p className="text-slate-500 mt-1">
            ملف شامل لكل عميل — يجمع العمليات، الأقساط، الوثائق، إسقاطات الدين، وكشف الحساب
          </p>
        </div>
      </div>

      {selectedClient && (
        <ClientDetail
          clientId={selectedClient}
          onBack={() => setSelectedClient(null)}
          user={user}
        />
      )}

      {!selectedClient && (
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="w-full grid grid-cols-5">
            <TabsTrigger value="dashboard" data-testid="dossier-tab-dashboard">📊 لوحة تحكم</TabsTrigger>
            <TabsTrigger value="clients" data-testid="dossier-tab-clients">👥 العملاء</TabsTrigger>
            <TabsTrigger value="waivers" data-testid="dossier-tab-waivers">💸 الإسقاطات</TabsTrigger>
            <TabsTrigger value="overdue" data-testid="dossier-tab-overdue">⏰ المتأخرات</TabsTrigger>
            <TabsTrigger value="audit" data-testid="dossier-tab-audit">🔍 سجل التدقيق</TabsTrigger>
          </TabsList>

          <TabsContent value="dashboard">
            <DashboardTab dashboard={dashboard} onOpenClient={setSelectedClient} />
          </TabsContent>
          <TabsContent value="clients">
            <ClientsTab onOpen={setSelectedClient} />
          </TabsContent>
          <TabsContent value="waivers">
            <WaiversTab onOpenClient={setSelectedClient} user={user} />
          </TabsContent>
          <TabsContent value="overdue">
            <OverdueTab onOpenClient={setSelectedClient} />
          </TabsContent>
          <TabsContent value="audit">
            <AuditTab />
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}

// ============================================================================
function DashboardTab({ dashboard, onOpenClient }) {
  if (!dashboard) return <div className="p-8 text-center text-slate-400">⏳ تحميل...</div>;
  const k = dashboard.kpi;
  const kpiCard = (label, val, color) => (
    <Card className={`border-r-4 ${color}`}>
      <CardContent className="p-4">
        <div className="text-xs text-slate-500">{label}</div>
        <div className="text-2xl font-bold mt-1" data-testid={`kpi-${label}`}>{fmt(val)}</div>
      </CardContent>
    </Card>
  );
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {kpiCard('عدد العملاء', k.total_clients, 'border-blue-500')}
        {kpiCard('إجمالي المبيعات', k.total_sales, 'border-emerald-500')}
        {kpiCard('التحصيل', k.total_collected, 'border-teal-500')}
        {kpiCard('المتبقي', k.total_remaining, 'border-amber-500')}
        {kpiCard('المتأخر', k.total_overdue, 'border-red-500')}
        {kpiCard('الإسقاطات', k.total_waived, 'border-purple-500')}
      </div>
      <Card>
        <CardHeader><CardTitle>حسب الفرع</CardTitle></CardHeader>
        <CardContent>
          <Table>
            <TableHeader><TableRow>
              <TableHead>الفرع</TableHead><TableHead>عدد العملاء</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {(dashboard.by_branch || []).map(b => (
                <TableRow key={b.agency_id}>
                  <TableCell>{b.agency_name}</TableCell>
                  <TableCell className="font-bold">{b.clients}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

// ============================================================================
function ClientsTab({ onOpen }) {
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('all');
  const [page, setPage] = useState(1);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    const token = localStorage.getItem('token');
    const params = { page, page_size: 25 };
    if (q) params.q = q;
    if (status && status !== 'all') params.status = status;
    axios.get(`${API}/client-dossier/list`, {
      headers: { Authorization: `Bearer ${token}` },
      params,
    }).then(r => {
      setItems(r.data.items || []);
      setTotal(r.data.total_matched || 0);
    }).catch(e => toast.error(e.message))
      .finally(() => setLoading(false));
  }, [q, status, page]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-[220px]">
          <Label>🔎 بحث</Label>
          <Input value={q} onChange={e => { setQ(e.target.value); setPage(1); }}
                  placeholder="اسم / هاتف / بطاقة تعريف"
                  data-testid="dossier-search-input" />
        </div>
        <div className="min-w-[180px]">
          <Label>حالة العميل</Label>
          <Select value={status} onValueChange={(v) => { setStatus(v); setPage(1); }}>
            <SelectTrigger data-testid="dossier-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">كل الحالات</SelectItem>
              <SelectItem value="fully_paid">🟢 مكتمل السداد</SelectItem>
              <SelectItem value="in_progress">🟡 قيد التسديد</SelectItem>
              <SelectItem value="no_payment">🔴 لم يدفع</SelectItem>
              <SelectItem value="overdue">⏰ متأخر</SelectItem>
              <SelectItem value="partially_waived">🟠 إعفاء جزئي</SelectItem>
              <SelectItem value="fully_waived">🟣 معفى كلياً</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="text-sm text-slate-500 self-end">
          {loading ? '⏳' : `عرض ${items.length} من ${total}`}
        </div>
      </div>

      <Card><CardContent className="p-0">
        <Table>
          <TableHeader><TableRow>
            <TableHead>العميل</TableHead>
            <TableHead>الهاتف</TableHead>
            <TableHead>العمليات</TableHead>
            <TableHead>خطط الأقساط</TableHead>
            <TableHead>المبيعات</TableHead>
            <TableHead>المدفوع</TableHead>
            <TableHead>المتبقي</TableHead>
            <TableHead>الحالة</TableHead>
            <TableHead>عرض</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {items.map(c => (
              <TableRow key={c.id} data-testid={`client-row-${c.id}`}>
                <TableCell className="font-medium">{c.name}</TableCell>
                <TableCell>{c.phone || '—'}</TableCell>
                <TableCell>{c.operation_count}</TableCell>
                <TableCell>{c.plan_count}</TableCell>
                <TableCell>{fmt(c.total_sales)}</TableCell>
                <TableCell className="text-emerald-700">{fmt(c.total_paid)}</TableCell>
                <TableCell className="text-red-700 font-bold">{fmt(c.total_remaining)}</TableCell>
                <TableCell>{c.status_flags.map(f => <span key={f}>{STATUS_BADGE(f)}</span>)}</TableCell>
                <TableCell>
                  <Button size="sm" variant="outline"
                            data-testid={`open-client-${c.id}`}
                            onClick={() => onOpen(c.id)}>📂 فتح</Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent></Card>

      <div className="flex justify-center gap-3">
        <Button variant="outline" disabled={page === 1}
                  onClick={() => setPage(p => Math.max(1, p-1))}>« سابق</Button>
        <span className="self-center">صفحة {page} / {Math.max(1, Math.ceil(total/25))}</span>
        <Button variant="outline" disabled={page*25 >= total}
                  onClick={() => setPage(p => p+1)}>التالي »</Button>
      </div>
    </div>
  );
}

// ============================================================================
function ClientDetail({ clientId, onBack, user }) {
  const [d, setD] = useState(null);
  const [subtab, setSubtab] = useState('overview');
  const [statement, setStatement] = useState(null);
  const [docs, setDocs] = useState([]);
  const [waiverDialog, setWaiverDialog] = useState(null);
  const [uploadDialog, setUploadDialog] = useState(false);

  const load = useCallback(() => {
    const token = localStorage.getItem('token');
    axios.get(`${API}/client-dossier/dossier/${clientId}`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(r => setD(r.data)).catch(e => toast.error(e.message));
  }, [clientId]);

  const loadStatement = useCallback(() => {
    const token = localStorage.getItem('token');
    axios.get(`${API}/client-dossier/dossier/${clientId}/statement`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(r => setStatement(r.data)).catch(e => toast.error(e.message));
  }, [clientId]);

  const loadDocs = useCallback(() => {
    const token = localStorage.getItem('token');
    axios.get(`${API}/client-dossier/dossier/${clientId}/documents`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(r => setDocs(r.data.documents || [])).catch(e => toast.error(e.message));
  }, [clientId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (subtab === 'statement' && !statement) loadStatement();
    if (subtab === 'documents') loadDocs();
  }, [subtab, statement, loadStatement, loadDocs]);

  if (!d) return <div className="p-8 text-center text-slate-400">⏳ تحميل...</div>;
  const c = d.client;

  return (
    <Card data-testid="client-detail-view">
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <Button variant="ghost" onClick={onBack} data-testid="dossier-back-btn">← رجوع</Button>
          <CardTitle className="mt-2">👤 {c.name}</CardTitle>
          <div className="text-sm text-slate-500 mt-1">
            {c.phone && <span>📞 {c.phone}   </span>}
            {c.cin_passport && <span>🆔 {c.cin_passport}</span>}
          </div>
          <div className="mt-2">{d.status_flags.map(f => <span key={f}>{STATUS_BADGE(f)}</span>)}</div>
        </div>
        <div className="text-right">
          <div className="text-xs text-slate-500">إجمالي المتبقي</div>
          <div className="text-2xl font-bold text-red-700" data-testid="detail-remaining">
            {fmt(d.totals.total_remaining)} د.ج
          </div>
          <div className="text-xs text-slate-500 mt-2">
            مدفوع {fmt(d.totals.total_paid)} · إسقاط {fmt(d.totals.total_waived)}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <Tabs value={subtab} onValueChange={setSubtab}>
          <TabsList className="grid grid-cols-7">
            <TabsTrigger value="overview" data-testid="subtab-overview">نظرة عامة</TabsTrigger>
            <TabsTrigger value="operations" data-testid="subtab-operations">العمليات</TabsTrigger>
            <TabsTrigger value="installments" data-testid="subtab-installments">الأقساط</TabsTrigger>
            <TabsTrigger value="documents" data-testid="subtab-documents">الوثائق</TabsTrigger>
            <TabsTrigger value="waivers" data-testid="subtab-waivers">الإسقاطات</TabsTrigger>
            <TabsTrigger value="statement" data-testid="subtab-statement">كشف حساب</TabsTrigger>
            <TabsTrigger value="audit" data-testid="subtab-audit">التدقيق</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-4 space-y-3">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="p-3 bg-slate-50 rounded"><div className="text-xs text-slate-500">المبيعات</div><div className="text-lg font-bold">{fmt(d.totals.total_sales)}</div></div>
              <div className="p-3 bg-emerald-50 rounded"><div className="text-xs text-slate-500">المدفوع</div><div className="text-lg font-bold text-emerald-700">{fmt(d.totals.total_paid)}</div></div>
              <div className="p-3 bg-purple-50 rounded"><div className="text-xs text-slate-500">الإسقاطات</div><div className="text-lg font-bold text-purple-700">{fmt(d.totals.total_waived)}</div></div>
              <div className="p-3 bg-red-50 rounded"><div className="text-xs text-slate-500">المتبقي</div><div className="text-lg font-bold text-red-700">{fmt(d.totals.total_remaining)}</div></div>
            </div>
            <div className="p-3 bg-blue-50 rounded text-sm">
              <b>📎 الوثائق:</b> إجمالي {d.documents.total} — ناقصة {d.documents.missing} — منتهية {d.documents.expired}
            </div>
            <div className="p-3 bg-slate-50 rounded text-sm space-y-1">
              <div><b>الجنس:</b> {c.gender || '—'}</div>
              <div><b>تاريخ الميلاد:</b> {c.birth_date || '—'}</div>
              <div><b>الولاية / البلدية:</b> {c.city || '—'}</div>
              <div><b>العنوان:</b> {c.address || '—'}</div>
              <div><b>ملاحظات:</b> {c.notes || '—'}</div>
            </div>
          </TabsContent>

          <TabsContent value="operations" className="mt-4">
            <Table>
              <TableHeader><TableRow>
                <TableHead>رقم</TableHead><TableHead>الخدمة</TableHead>
                <TableHead>المصدر</TableHead><TableHead>الإجمالي</TableHead>
                <TableHead>المدفوع</TableHead><TableHead>الإسقاط</TableHead>
                <TableHead>المتبقي</TableHead><TableHead>الحالة</TableHead>
                <TableHead>إجراء</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {d.operations.map(op => (
                  <TableRow key={op.id} data-testid={`op-row-${op.id}`}>
                    <TableCell className="font-medium">{op.operation_no}</TableCell>
                    <TableCell>{op.service_name || '—'}</TableCell>
                    <TableCell><Badge variant="outline">{op.operation_source}</Badge></TableCell>
                    <TableCell>{fmt(op.total_amount)}</TableCell>
                    <TableCell className="text-emerald-700">{fmt(op.paid_amount)}</TableCell>
                    <TableCell className="text-purple-700">{fmt(op.waived_amount)}</TableCell>
                    <TableCell className="text-red-700 font-bold">{fmt(op.remaining_amount)}</TableCell>
                    <TableCell>{STATUS_BADGE(op.computed_status === 'paid' ? 'fully_paid' :
                                                     op.computed_status === 'partial' ? 'in_progress' : 'no_payment')}</TableCell>
                    <TableCell>
                      {op.remaining_amount > 0 && (
                        <Button size="sm" variant="outline"
                                  data-testid={`waiver-btn-${op.id}`}
                                  onClick={() => setWaiverDialog({op})}>
                          💸 إسقاط
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {d.operations.length === 0 && (
                  <TableRow><TableCell colSpan={9} className="text-center text-slate-400 py-6">لا توجد عمليات مباشرة (قد تحتوي على خطط أقساط في تبويب الأقساط)</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TabsContent>

          <TabsContent value="installments" className="mt-4 space-y-3">
            {d.installment_plans.map(p => (
              <Card key={p.id} data-testid={`plan-card-${p.id}`}>
                <CardHeader>
                  <div className="flex justify-between">
                    <div>
                      <div className="font-bold">{p.receipt_no}</div>
                      <div className="text-sm text-slate-500">{p.service_name}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-slate-500">المتبقي</div>
                      <div className="text-lg font-bold text-red-700">{fmt(p.remaining_amount)}</div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-sm mb-3">
                    الإجمالي: <b>{fmt(p.total_amount)}</b> ·
                    الدفعة الأولى: <b>{fmt(p.first_payment)}</b> ·
                    عدد الأقساط: <b>{p.number_of_payments}</b> ·
                    التكرار: <b>{p.payment_frequency}</b>
                  </div>
                  <Table>
                    <TableHeader><TableRow>
                      <TableHead>#</TableHead><TableHead>تاريخ الاستحقاق</TableHead>
                      <TableHead>المستحق</TableHead><TableHead>المدفوع</TableHead>
                      <TableHead>الحالة</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {p.payments.map(pay => (
                        <TableRow key={pay.id}>
                          <TableCell>{pay.payment_number}</TableCell>
                          <TableCell>{pay.due_date ? new Date(pay.due_date).toLocaleDateString('en') : '—'}</TableCell>
                          <TableCell>{fmt(pay.amount_due)}</TableCell>
                          <TableCell className="text-emerald-700">{fmt(pay.amount_paid)}</TableCell>
                          <TableCell><Badge>{pay.status}</Badge></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            ))}
            {d.installment_plans.length === 0 && (
              <div className="text-center text-slate-400 py-6">لا توجد خطط أقساط</div>
            )}
          </TabsContent>

          <TabsContent value="documents" className="mt-4 space-y-3">
            <div className="flex justify-between items-center">
              <div className="text-sm text-slate-500">
                {docs.length} وثيقة · {docs.filter(x => x.status === 'archived').length} مؤرشفة
              </div>
              <Button onClick={() => setUploadDialog(true)} data-testid="upload-doc-btn">
                ➕ رفع وثيقة
              </Button>
            </div>
            <Table>
              <TableHeader><TableRow>
                <TableHead>التصنيف</TableHead><TableHead>النوع</TableHead>
                <TableHead>الملف</TableHead><TableHead>الإصدار</TableHead>
                <TableHead>تاريخ الرفع</TableHead><TableHead>الانتهاء</TableHead>
                <TableHead>الحالة</TableHead><TableHead>إجراء</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {docs.map(dc => (
                  <TableRow key={dc.id}>
                    <TableCell><Badge variant="outline">{DOC_CATEGORIES.find(x=>x.value===dc.category)?.label || dc.category}</Badge></TableCell>
                    <TableCell>{dc.document_type}</TableCell>
                    <TableCell className="max-w-[240px] truncate">{dc.file_name}</TableCell>
                    <TableCell>v{dc.version}{dc.is_current ? ' 🟢' : ''}</TableCell>
                    <TableCell>{dc.uploaded_at ? new Date(dc.uploaded_at).toLocaleDateString('en') : '—'}</TableCell>
                    <TableCell>{dc.expiry_date ? new Date(dc.expiry_date).toLocaleDateString('en') : '—'}</TableCell>
                    <TableCell><Badge>{dc.status}</Badge></TableCell>
                    <TableCell className="space-x-1">
                      <Button size="sm" variant="outline"
                                data-testid={`doc-download-${dc.id}`}
                                onClick={() => downloadDoc(dc.id)}>⬇️</Button>
                      {['super_admin','general_manager','general_accountant','accountant'].includes(user?.role) && dc.status !== 'archived' && (
                        <Button size="sm" variant="destructive"
                                  data-testid={`doc-archive-${dc.id}`}
                                  onClick={() => archiveDoc(dc.id, loadDocs)}>🗑️</Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {docs.length === 0 && (
                  <TableRow><TableCell colSpan={8} className="text-center text-slate-400 py-6">لا توجد وثائق مرفوعة</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TabsContent>

          <TabsContent value="waivers" className="mt-4">
            <ClientWaivers clientId={clientId} user={user} onChange={load} />
          </TabsContent>

          <TabsContent value="statement" className="mt-4">
            {!statement ? (
              <div className="text-center text-slate-400 py-6">⏳ تحميل كشف الحساب...</div>
            ) : (
              <StatementView statement={statement} />
            )}
          </TabsContent>

          <TabsContent value="audit" className="mt-4">
            <AuditTab clientId={clientId} />
          </TabsContent>
        </Tabs>
      </CardContent>

      {waiverDialog && (
        <WaiverDialog
          clientId={clientId}
          operation={waiverDialog.op}
          onClose={() => setWaiverDialog(null)}
          onDone={() => { setWaiverDialog(null); load(); setStatement(null); }}
        />
      )}
      {uploadDialog && (
        <UploadDocDialog
          clientId={clientId}
          onClose={() => setUploadDialog(false)}
          onDone={() => { setUploadDialog(false); loadDocs(); load(); }}
        />
      )}
    </Card>
  );
}

// ============================================================================
async function downloadDoc(docId) {
  const token = localStorage.getItem('token');
  try {
    const r = await axios.get(`${API}/client-dossier/documents/${docId}/download`, {
      headers: { Authorization: `Bearer ${token}` }, responseType: 'blob',
    });
    const url = URL.createObjectURL(new Blob([r.data]));
    window.open(url, '_blank');
  } catch (e) {
    toast.error('فشل تحميل الوثيقة');
  }
}

async function archiveDoc(docId, reload) {
  if (!window.confirm('هل تريد أرشفة هذه الوثيقة؟ يمكن استرجاعها لاحقاً.')) return;
  const token = localStorage.getItem('token');
  try {
    await axios.delete(`${API}/client-dossier/documents/${docId}`, {
      headers: { Authorization: `Bearer ${token}` },
      params: { reason: 'أرشفة من ملف العميل' },
    });
    toast.success('تمت الأرشفة');
    reload && reload();
  } catch (e) {
    toast.error('فشلت الأرشفة');
  }
}

// ============================================================================
function StatementView({ statement }) {
  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <div className="font-bold text-lg">كشف حساب: {statement.client.name}</div>
        <div className="text-lg font-bold text-red-700">
          الرصيد النهائي: {fmt(statement.final_balance)} د.ج
        </div>
      </div>
      <Table>
        <TableHeader><TableRow>
          <TableHead>التاريخ</TableHead><TableHead>البيان</TableHead>
          <TableHead>المرجع</TableHead><TableHead>مدين</TableHead>
          <TableHead>مدفوع</TableHead><TableHead>إسقاط</TableHead>
          <TableHead>الرصيد</TableHead>
        </TableRow></TableHeader>
        <TableBody>
          {statement.events.map((e, i) => (
            <TableRow key={i} data-testid={`stmt-row-${i}`}>
              <TableCell>{e.date ? new Date(e.date).toLocaleDateString('en') : '—'}</TableCell>
              <TableCell>{e.description}</TableCell>
              <TableCell className="text-xs">{e.operation_no}</TableCell>
              <TableCell className="text-slate-700">{e.debit ? fmt(e.debit) : ''}</TableCell>
              <TableCell className="text-emerald-700">{e.payment ? fmt(e.payment) : ''}</TableCell>
              <TableCell className="text-purple-700">{e.waiver ? fmt(e.waiver) : ''}</TableCell>
              <TableCell className={`font-bold ${e.balance > 0 ? 'text-red-700' : 'text-emerald-700'}`}>
                {fmt(e.balance)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <div className="text-xs text-slate-400">
        تم توليد الكشف في: {new Date(statement.generated_at).toLocaleString('en')}
      </div>
    </div>
  );
}

// ============================================================================
function ClientWaivers({ clientId, user, onChange }) {
  const [items, setItems] = useState([]);
  const load = useCallback(() => {
    const token = localStorage.getItem('token');
    axios.get(`${API}/client-dossier/waivers`, {
      headers: { Authorization: `Bearer ${token}` },
      params: { client_id: clientId },
    }).then(r => setItems(r.data || []));
  }, [clientId]);
  useEffect(() => { load(); }, [load]);

  const canApprove = ['super_admin', 'general_manager', 'general_accountant'].includes(user?.role);
  const act = async (id, action, extra = {}) => {
    const token = localStorage.getItem('token');
    try {
      await axios.put(`${API}/client-dossier/waivers/${id}/${action}`, null, {
        headers: { Authorization: `Bearer ${token}` }, params: extra,
      });
      toast.success('تم');
      load(); onChange && onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'فشل');
    }
  };

  return (
    <Table>
      <TableHeader><TableRow>
        <TableHead>رقم الإسقاط</TableHead><TableHead>العملية</TableHead>
        <TableHead>المبلغ</TableHead><TableHead>السبب</TableHead>
        <TableHead>الحالة</TableHead><TableHead>التاريخ</TableHead>
        <TableHead>إجراء</TableHead>
      </TableRow></TableHeader>
      <TableBody>
        {items.map(w => (
          <TableRow key={w.id} data-testid={`waiver-row-${w.id}`}>
            <TableCell className="font-medium">{w.waiver_no}</TableCell>
            <TableCell className="text-xs">{w.operation_source}: {w.operation_id?.slice(0,8)}...</TableCell>
            <TableCell className="text-purple-700 font-bold">{fmt(w.amount)}</TableCell>
            <TableCell>{WAIVER_REASONS.find(r=>r.value===w.reason_category)?.label} — {w.reason}</TableCell>
            <TableCell><Badge>{w.status}</Badge></TableCell>
            <TableCell>{w.created_at ? new Date(w.created_at).toLocaleDateString('en') : '—'}</TableCell>
            <TableCell className="space-x-1">
              {w.status === 'pending' && canApprove && (
                <>
                  <Button size="sm" variant="default"
                            data-testid={`waiver-approve-${w.id}`}
                            onClick={() => window.confirm(`اعتماد إسقاط ${fmt(w.amount)} د.ج؟`) && act(w.id, 'approve')}>
                    ✅ اعتماد
                  </Button>
                  <Button size="sm" variant="destructive"
                            data-testid={`waiver-reject-${w.id}`}
                            onClick={() => act(w.id, 'reject', {reason:'مرفوض'})}>
                    ❌ رفض
                  </Button>
                </>
              )}
            </TableCell>
          </TableRow>
        ))}
        {items.length === 0 && (
          <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-6">لا يوجد إسقاطات لهذا العميل</TableCell></TableRow>
        )}
      </TableBody>
    </Table>
  );
}

// ============================================================================
function WaiverDialog({ clientId, operation, onClose, onDone }) {
  const [amount, setAmount] = useState(operation.remaining_amount || '');
  const [reason, setReason] = useState('');
  const [category, setCategory] = useState('other');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!reason.trim()) return toast.error('اكتب سبب الإسقاط');
    if (parseFloat(amount) <= 0) return toast.error('المبلغ يجب أن يكون موجباً');
    if (parseFloat(amount) > operation.remaining_amount + 0.01)
      return toast.error(`المبلغ لا يتجاوز المتبقي (${fmt(operation.remaining_amount)})`);
    setBusy(true);
    const token = localStorage.getItem('token');
    try {
      await axios.post(`${API}/client-dossier/waivers`, {
        client_id: clientId,
        operation_id: operation.id,
        operation_source: operation.operation_source,
        amount: parseFloat(amount),
        reason,
        reason_category: category,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('تم تسجيل طلب الإسقاط — بانتظار اعتماد المدير/المحاسب العام');
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'فشل تسجيل الإسقاط');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="waiver-dialog">
        <DialogHeader>
          <DialogTitle>💸 تسجيل إسقاط دين</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="p-3 bg-slate-50 rounded text-sm">
            العملية: <b>{operation.operation_no}</b><br />
            الإجمالي: <b>{fmt(operation.total_amount)}</b> · المدفوع: <b>{fmt(operation.paid_amount)}</b><br />
            <span className="text-red-700">المتبقي: <b>{fmt(operation.remaining_amount)}</b></span>
          </div>
          <div>
            <Label>المبلغ *</Label>
            <Input type="number" step="0.01" value={amount}
                    onChange={e => setAmount(e.target.value)}
                    data-testid="waiver-amount-input" />
          </div>
          <div>
            <Label>سبب الإسقاط *</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="waiver-category-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {WAIVER_REASONS.map(r => (
                  <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>تفصيل السبب *</Label>
            <Textarea value={reason} onChange={e => setReason(e.target.value)}
                        placeholder="وصف مفصل للحالة (سيظهر في السجل)"
                        data-testid="waiver-reason-input" />
          </div>
          <div className="text-xs text-amber-600 p-2 bg-amber-50 rounded">
            ⚠️ الإسقاط لا يخصم من الرصيد إلا بعد اعتماد المدير العام أو المحاسب العام.
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>إلغاء</Button>
          <Button onClick={submit} disabled={busy} data-testid="waiver-submit-btn">
            {busy ? '⏳' : '💾 حفظ الطلب'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
function UploadDocDialog({ clientId, onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [category, setCategory] = useState('personal');
  const [type, setType] = useState('');
  const [expiry, setExpiry] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!file || !type.trim()) return toast.error('اختر ملف واكتب نوع الوثيقة');
    setBusy(true);
    const token = localStorage.getItem('token');
    const fd = new FormData();
    fd.append('file', file);
    fd.append('category', category);
    fd.append('document_type', type);
    if (expiry) fd.append('expiry_date', expiry);
    if (notes) fd.append('notes', notes);
    try {
      await axios.post(`${API}/client-dossier/dossier/${clientId}/documents`, fd, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('تم رفع الوثيقة');
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'فشل الرفع');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="upload-doc-dialog">
        <DialogHeader><DialogTitle>📎 رفع وثيقة</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>الملف *</Label>
            <Input type="file" onChange={e => setFile(e.target.files[0])}
                    data-testid="doc-file-input" />
          </div>
          <div>
            <Label>التصنيف *</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="doc-category-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {DOC_CATEGORIES.map(c => (
                  <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>نوع الوثيقة * (مثال: بطاقة تعريف / عقد / صورة)</Label>
            <Input value={type} onChange={e => setType(e.target.value)}
                    data-testid="doc-type-input" />
          </div>
          <div>
            <Label>تاريخ انتهاء الصلاحية (اختياري)</Label>
            <Input type="date" value={expiry} onChange={e => setExpiry(e.target.value)}
                    data-testid="doc-expiry-input" />
          </div>
          <div>
            <Label>ملاحظات</Label>
            <Textarea value={notes} onChange={e => setNotes(e.target.value)}
                        data-testid="doc-notes-input" />
          </div>
          <div className="text-xs text-blue-600 p-2 bg-blue-50 rounded">
            💡 إذا رفعت وثيقة بنفس النوع، ستُحفظ النسخة القديمة تلقائياً كإصدار سابق (Versioning).
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>إلغاء</Button>
          <Button onClick={submit} disabled={busy} data-testid="doc-upload-submit-btn">
            {busy ? '⏳ جاري الرفع...' : '📤 رفع'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
function WaiversTab({ onOpenClient, user }) {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState('pending');
  const load = useCallback(() => {
    const token = localStorage.getItem('token');
    axios.get(`${API}/client-dossier/waivers`, {
      headers: { Authorization: `Bearer ${token}` },
      params: status !== 'all' ? { status } : {},
    }).then(r => setItems(r.data || []));
  }, [status]);
  useEffect(() => { load(); }, [load]);

  const canApprove = ['super_admin', 'general_manager', 'general_accountant'].includes(user?.role);
  const act = async (id, action, extra = {}) => {
    const token = localStorage.getItem('token');
    try {
      await axios.put(`${API}/client-dossier/waivers/${id}/${action}`, null, {
        headers: { Authorization: `Bearer ${token}` }, params: extra,
      });
      toast.success('تم');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'فشل');
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex gap-3 items-end">
        <div>
          <Label>الحالة</Label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">الكل</SelectItem>
              <SelectItem value="pending">🟡 بانتظار الاعتماد</SelectItem>
              <SelectItem value="approved">✅ معتمد</SelectItem>
              <SelectItem value="rejected">❌ مرفوض</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <Card><CardContent className="p-0"><Table>
        <TableHeader><TableRow>
          <TableHead>الرقم</TableHead><TableHead>العميل</TableHead>
          <TableHead>المبلغ</TableHead><TableHead>السبب</TableHead>
          <TableHead>الحالة</TableHead><TableHead>التاريخ</TableHead>
          <TableHead>إجراء</TableHead>
        </TableRow></TableHeader>
        <TableBody>
          {items.map(w => (
            <TableRow key={w.id}>
              <TableCell className="font-medium">{w.waiver_no}</TableCell>
              <TableCell>
                <button className="text-blue-700 hover:underline"
                          onClick={() => onOpenClient(w.client_id)}>
                  {w.client_name}
                </button>
              </TableCell>
              <TableCell className="text-purple-700 font-bold">{fmt(w.amount)}</TableCell>
              <TableCell>{WAIVER_REASONS.find(r=>r.value===w.reason_category)?.label}</TableCell>
              <TableCell><Badge>{w.status}</Badge></TableCell>
              <TableCell>{w.created_at ? new Date(w.created_at).toLocaleDateString('en') : '—'}</TableCell>
              <TableCell className="space-x-1">
                {w.status === 'pending' && canApprove && (
                  <>
                    <Button size="sm"
                              onClick={() => window.confirm(`اعتماد ${fmt(w.amount)} د.ج؟`) && act(w.id, 'approve')}>✅</Button>
                    <Button size="sm" variant="destructive"
                              onClick={() => act(w.id, 'reject', {reason:'مرفوض'})}>❌</Button>
                  </>
                )}
              </TableCell>
            </TableRow>
          ))}
          {items.length === 0 && (
            <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-6">لا توجد طلبات إسقاط</TableCell></TableRow>
          )}
        </TableBody>
      </Table></CardContent></Card>
    </div>
  );
}

// ============================================================================
function OverdueTab({ onOpenClient }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    const token = localStorage.getItem('token');
    axios.get(`${API}/client-dossier/reports/overdue-aging`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(r => setData(r.data)).catch(e => toast.error(e.message));
  }, []);
  if (!data) return <div className="p-8 text-center text-slate-400">⏳</div>;
  const bucketCard = (key, label, color) => (
    <Card className={`border-r-4 ${color}`}>
      <CardContent className="p-4">
        <div className="text-xs text-slate-500">{label}</div>
        <div className="text-xl font-bold">{fmt(data.buckets[key].amount)}</div>
        <div className="text-xs">{data.buckets[key].count} قسط · {data.buckets[key].unique_clients} عميل</div>
      </CardContent>
    </Card>
  );
  return (
    <div className="space-y-4">
      <div className="text-lg font-bold">
        إجمالي المتأخرات: <span className="text-red-700">{fmt(data.total_overdue_amount)} د.ج</span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {bucketCard('1_7', '1-7 أيام', 'border-amber-400')}
        {bucketCard('8_30', '8-30 يوم', 'border-orange-500')}
        {bucketCard('31_60', '31-60 يوم', 'border-red-500')}
        {bucketCard('60_plus', '+60 يوم', 'border-red-800')}
      </div>
      <Card><CardContent className="p-0">
        <Table>
          <TableHeader><TableRow>
            <TableHead>العميل</TableHead><TableHead>الوصل</TableHead>
            <TableHead>القسط #</TableHead><TableHead>الاستحقاق</TableHead>
            <TableHead>المستحق</TableHead><TableHead>المدفوع</TableHead>
            <TableHead>المتأخر</TableHead><TableHead>الأيام</TableHead>
            <TableHead>إجراء</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {data.items.map((it, i) => (
              <TableRow key={i} data-testid={`overdue-row-${i}`}>
                <TableCell>{it.client_name}</TableCell>
                <TableCell className="text-xs">{it.receipt_no}</TableCell>
                <TableCell>{it.payment_number}</TableCell>
                <TableCell>{it.due_date ? new Date(it.due_date).toLocaleDateString('en') : '—'}</TableCell>
                <TableCell>{fmt(it.amount_due)}</TableCell>
                <TableCell className="text-emerald-700">{fmt(it.amount_paid)}</TableCell>
                <TableCell className="text-red-700 font-bold">{fmt(it.overdue_amount)}</TableCell>
                <TableCell>
                  <Badge className={it.days_overdue > 60 ? 'bg-red-200 text-red-900' : 'bg-amber-100'}>
                    {it.days_overdue} يوم
                  </Badge>
                </TableCell>
                <TableCell>
                  <Button size="sm" variant="outline"
                            onClick={() => onOpenClient(it.client_id)}>📂</Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent></Card>
    </div>
  );
}

// ============================================================================
function AuditTab({ clientId }) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    const token = localStorage.getItem('token');
    const params = clientId ? { client_id: clientId, limit: 200 } : { limit: 200 };
    axios.get(`${API}/client-dossier/audit-log`, {
      headers: { Authorization: `Bearer ${token}` }, params,
    }).then(r => setItems(r.data || [])).catch(() => {});
  }, [clientId]);
  return (
    <Table>
      <TableHeader><TableRow>
        <TableHead>التاريخ</TableHead><TableHead>الحدث</TableHead>
        <TableHead>المستخدم</TableHead><TableHead>الكيان</TableHead>
        <TableHead>السبب</TableHead>
      </TableRow></TableHeader>
      <TableBody>
        {items.map(a => (
          <TableRow key={a.id}>
            <TableCell className="text-xs">{a.timestamp ? new Date(a.timestamp).toLocaleString('en') : '—'}</TableCell>
            <TableCell><Badge variant="outline">{a.action}</Badge></TableCell>
            <TableCell>{a.user_name} <span className="text-xs text-slate-400">({a.user_role})</span></TableCell>
            <TableCell className="text-xs">{a.entity_type}: {a.entity_id?.slice(0,8)}</TableCell>
            <TableCell className="text-xs">{a.reason || '—'}</TableCell>
          </TableRow>
        ))}
        {items.length === 0 && (
          <TableRow><TableCell colSpan={5} className="text-center text-slate-400 py-6">لا يوجد سجل تدقيق</TableCell></TableRow>
        )}
      </TableBody>
    </Table>
  );
}
